import React, { useState, useEffect, useContext } from 'react';
import { Button, FormField, Input, Select, SpaceBetween, FileUpload } from '@cloudscape-design/components';
import ModalsContext from '../../../../domain/providers/modal/ModalsContext';
import ModalsContextType from '../../../../domain/providers/modal/ModalsContextType';
import UserContext from '../../../../domain/providers/user/UserContext';
import UserContextType from '../../../../domain/providers/user/UserContextType';
import di from '../../../../di/DependencyInjection';
import { UploadChannelUseCase, UploadChannelUseCaseName } from '../../../../domain/use_cases/ai/UploadChannelUseCase';
import { GetAICustomersUseCase, GetAICustomersUseCaseName } from '../../../../domain/use_cases/ai/GetAICustomersUseCase';
import { isRight, isLeft } from 'fp-ts/lib/Either';
import LoadingComponent from '../../../components/LoadingComponent/LoadingComponent';

interface UploadChannelModalProps {
    defaultChannel?: 'calls' | 'chats' | 'emails';
    onSuccess?: () => void;
}

const UploadChannelModal: React.FC<UploadChannelModalProps> = ({ defaultChannel, onSuccess }) => {
    const { closeModalCustom, addToast } = useContext(ModalsContext) as ModalsContextType;
    const { user } = useContext(UserContext) as UserContextType;
    
    const [channel, setChannel] = useState<'calls' | 'chats' | 'emails'>(defaultChannel || 'calls');
    const [customer, setCustomer] = useState<string>('');
    const [lob, setLob] = useState<string>('');
    const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    
    const [customers, setCustomers] = useState<string[]>([]);
    const [lobs, setLobs] = useState<Record<string, string[]>>({});
    const [lobOptions, setLobOptions] = useState<string[]>([]);
    const [loadingData, setLoadingData] = useState(false);

    // Cargar customers y LOBs cuando cambia el channel
    useEffect(() => {
        const loadCustomers = async () => {
            setLoadingData(true);
            try {
                const response = await di.get<GetAICustomersUseCase>(GetAICustomersUseCaseName)
                    ._aiRepository.getAICustomers(channel);
                
                if (isRight(response)) {
                    const result = JSON.parse(response.right as string);
                    const keysArray = Object.keys(result.data);
                    setCustomers(keysArray);
                    setLobs(result.data);
                }
            } catch (error) {
                console.error('Error loading customers:', error);
            } finally {
                setLoadingData(false);
            }
        };
        
        loadCustomers();
    }, [channel]);

    // Cargar LOBs cuando cambia el customer
    useEffect(() => {
        if (customer && lobs[customer]) {
            setLobOptions(lobs[customer]);
            setLob(''); // Reset LOB when customer changes
        } else {
            setLobOptions([]);
            setLob('');
        }
    }, [customer, lobs]);


    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validar tipo de archivo según el canal
            const validExtensions = channel === 'calls' ? ['.mp3', '.wav', '.m4a', '.zip'] : ['.txt', '.zip'];
            const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
            
            if (!validExtensions.includes(fileExtension)) {
                addToast(
                    `Invalid file type. Expected ${channel === 'calls' ? 'audio files (.mp3, .wav, .m4a) or .zip' : 'text files (.txt) or .zip'}`,
                    'error',
                    undefined
                );
                return;
            }
            
            setSelectedFile(file);
        }
    };

    const handleSubmit = async () => {
        // Validaciones
        if (!selectedFile) {
            addToast('Please select a file to upload', 'error', undefined);
            return;
        }
        
        if (!channel) {
            addToast('Please select a channel', 'error', undefined);
            return;
        }
        
        if (!customer) {
            addToast('Please select a customer', 'error', undefined);
            return;
        }
        
        if (!lob) {
            addToast('Please select a LOB', 'error', undefined);
            return;
        }

        setIsUploading(true);

        try {
            const uploadChannelUseCase = di.get<UploadChannelUseCase>(UploadChannelUseCaseName);
            
            // Obtener user_id del usuario actual
            // Usar user?.id (UUID de Cognito) o user?.user_id (ID numérico de BD)
            const currentUserId = user?.id || user?.user_id?.toString();
            
            if (!currentUserId) {
                addToast('User ID not available. Please log in again.', 'error', undefined);
                setIsUploading(false);
                return;
            }
            
            // Asegurar que la fecha esté en formato YYYY-MM-DD (ejemplo: 2025-12-01)
            let formattedDate: string;
            if (date) {
                // Si la fecha ya está en formato YYYY-MM-DD, usarla directamente
                // Si no, convertirla al formato correcto
                const dateObj = new Date(date);
                if (isNaN(dateObj.getTime())) {
                    // Si la fecha es inválida, usar la fecha actual
                    formattedDate = new Date().toISOString().split('T')[0];
                } else {
                    // Formatear como YYYY-MM-DD
                    const year = dateObj.getFullYear();
                    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const day = String(dateObj.getDate()).padStart(2, '0');
                    formattedDate = `${year}-${month}-${day}`;
                }
            } else {
                // Si no hay fecha, usar la fecha actual
                formattedDate = new Date().toISOString().split('T')[0];
            }
            
            const response = await uploadChannelUseCase.call({
                customer,
                lob,
                user_id: currentUserId,
                date: formattedDate,
                channel,
                file: selectedFile
            });

            if (isRight(response)) {
                addToast('File uploaded successfully', 'success', undefined);
                closeModalCustom();
                if (onSuccess) {
                    onSuccess();
                }
            } else if (isLeft(response)) {
                const error = response.left;
                addToast((error as any)?.message || 'Failed to upload file', 'error', undefined);
            }
        } catch (error: any) {
            console.error('Upload error:', error);
            addToast(
                error && typeof error === "object" && "message" in error && error.message
                    ? error.message
                    : "An unexpected error occurred",
                "error",
                undefined
            );
        } finally {
            //setIsUploading(false);
        }
    };

    const channelOptions = [
        { label: 'Calls', value: 'calls' },
        { label: 'Chats', value: 'chats' },
        { label: 'Emails', value: 'emails' }
    ];

    const customerOptions = customers.map(c => ({ label: c, value: c }));
    const lobOptionsSelect = lobOptions.map(l => ({ label: l, value: l }));

    return (
        <div style={{ paddingRight: '50px', minWidth: '500px' }}>
            <SpaceBetween size="m">
                <FormField label="Channel" description="Select the channel type">
                    <Select
                        selectedOption={channelOptions.find(opt => opt.value === channel) || null}
                        onChange={({ detail }) => {
                            setChannel(detail.selectedOption.value as 'calls' | 'chats' | 'emails');
                            setCustomer('');
                            setLob('');
                            setSelectedFile(null);
                        }}
                        options={channelOptions}
                    />
                </FormField>

                <FormField label="Customer" description="Select the customer">
                    {loadingData ? (
                        <LoadingComponent />
                    ) : (
                        <Select
                            selectedOption={customer ? { label: customer, value: customer } : null}
                            onChange={({ detail }) => setCustomer(detail.selectedOption.value || '')}
                            options={customerOptions}
                            placeholder="Select a customer"
                        />
                    )}
                </FormField>

                <FormField label="LOB (Line of Business)" description="Select the LOB">
                    {loadingData ? (
                        <LoadingComponent />
                    ) : (
                        <Select
                            selectedOption={lob ? { label: lob, value: lob } : null}
                            onChange={({ detail }) => setLob(detail.selectedOption.value || '')}
                            options={lobOptionsSelect}
                            placeholder="Select a LOB"
                            disabled={!customer}
                        />
                    )}
                </FormField>

                <FormField 
                    label="File" 
                    description={channel === 'calls' 
                        ? 'Upload an audio file (.mp3, .wav, .m4a) or .zip' 
                        : 'Upload a text file (.txt) or .zip'}
                >
                    <input
                        type="file"
                        accept={channel === 'calls' ? '.mp3,.wav,.m4a,.zip' : '.txt,.zip'}
                        onChange={handleFileChange}
                        style={{ width: '100%', padding: '8px' }}
                    />
                    {selectedFile && (
                        <div style={{ marginTop: '8px', color: '#666' }}>
                            Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                        </div>
                    )}
                </FormField>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                    <Button variant="link" onClick={closeModalCustom} disabled={isUploading}>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSubmit}
                        disabled={isUploading || !selectedFile || !customer || !lob}
                    >
                        {isUploading ? 'Uploading...' : 'Upload'}
                    </Button>
                </div>
            </SpaceBetween>
        </div>
    );
};

export default UploadChannelModal;

